// Contrat de synchronisation terrain (lot L3, cadrage section 4, D-041, D-051) : posé par la partie A,
// utilisé par la partie B (tournées, constats : même journal d'opérations) et par la partie C
// (application terrain hors connexion). Chaque opération porte un identifiant client (UUID, v7
// recommandé) généré sur l'appareil, l'objet visé et la version attendue de cet objet ; le serveur
// applique, ignore un doublon (identifiant déjà reçu) ou déclare un conflit (version serveur différente :
// réaffectation, annulation, clôture) ; dans ce cas l'opération est conservée (rapport ou constat en
// état « conflit ») et rien n'est écrasé ; le responsable technique tranche (retenir la version de
// l'appareil ou l'écarter), les deux versions restant lisibles (R9).
//
// Règle de version (D-130, commune au serveur et à l'appareil) : la version d'un objet emporté (mission,
// tournée, ordre de mission) n'avance que lorsque le bureau change ce que l'agent doit faire ou met fin
// à l'objet (affectation ou réaffectation, consignes, date, points ou horaire d'une tournée, annulation,
// clôture, validation, décision sur un conflit) ; jamais à l'application d'une opération de l'agent ni
// aux changements d'état qui en découlent (en cours, rapport reçu, constat reçu, conflit), ni au
// démarrage d'un ordre de mission. Chaque opération garde la version connue de l'appareil au moment de
// la saisie ; l'appareil reporte sur l'objet local la version renvoyée pour une opération appliquée ou
// ignorée en doublon, jamais celle d'un conflit. Deux saisies hors connexion sur la même mission ne se
// contredisent donc pas ; une réaffectation pendant la déconnexion produit un conflit (R9).
import { z } from 'zod';
import { schemaDateHeure } from './commun';
import { schemaFormulaireTerrain, schemaOrdreMissionTerrain } from './inspections';
import {
  CODES_CATEGORIES_SIGNALEMENT,
  CODES_PRIORITES_INTERVENTION,
  ETATS_MISSION,
  ETATS_RAPPORT_MISSION,
  schemaContenuRapportMission,
  TYPES_RAPPORT_MISSION,
} from './interventions';
import { schemaPosition } from './marches-structure';
import { schemaTourneeTerrain } from './proprete';

const uuid = z.string().uuid('Identifiant invalide');

/** Identifiant généré sur l'appareil (UUID) : clé d'idempotence d'une opération ou d'une photo. */
export const schemaIdClient = uuid;

/** Types d'opérations de synchronisation (cadrage section 4) ; la partie A applique rapport_mission, debut_mission et position, la partie B realisation_tournee et constat. */
export const TYPES_OPERATION_TERRAIN = ['rapport_mission', 'debut_mission', 'realisation_tournee', 'constat', 'position', 'position_place', 'releve_ilot', 'recensement_place'] as const;
export type TypeOperationTerrain = (typeof TYPES_OPERATION_TERRAIN)[number];

/** Objets visés par une opération : mission (A), tournée et ordre de mission (B). */
export const OBJETS_OPERATION_TERRAIN = ['mission', 'tournee', 'ordre_mission', 'emplacement', 'ilot_marche'] as const;
export type ObjetOperationTerrain = (typeof OBJETS_OPERATION_TERRAIN)[number];

/** Résultat d'une opération reçue (journal metier.operation_synchronisation). */
export const RESULTATS_OPERATION_TERRAIN = ['appliquee', 'ignoree_doublon', 'conflit', 'rejetee'] as const;
export type CodeResultatOperationTerrain = (typeof RESULTATS_OPERATION_TERRAIN)[number];

/** Objets auxquels une photo de terrain se rattache (D-051 : documents de la collectivité). */
export const OBJETS_PHOTO_TERRAIN = ['signalement', 'rapport_mission', 'constat', 'realisation_tournee', 'emplacement'] as const;
export type ObjetPhotoTerrain = (typeof OBJETS_PHOTO_TERRAIN)[number];

/** Nombre maximal d'opérations par envoi et taille visée de la charge de travail (moins de 2 Mo sans photos). */
export const MAX_OPERATIONS_PAR_ENVOI = 200;
export const LIMITE_TRAVAIL_PAR_OBJET = 100;

// Charges par type d'opération ----------------------------------------------------------------

/** Rapport de mission saisi sur l'appareil ; les photos sont désignées par leur identifiant client (envoyées par POST /terrain/photos). */
export const schemaChargeRapportMission = z.strictObject({
  type: z.enum(TYPES_RAPPORT_MISSION),
  contenu: schemaContenuRapportMission,
  photos: z.array(uuid).max(20).default([]),
  /** Position jointe par l'agent (D-121) : conservée au journal des opérations seulement, jamais affichée aux usagers (D-061). */
  position: schemaPosition.optional(),
});
export type ChargeRapportMission = z.infer<typeof schemaChargeRapportMission>;

/** Début d'une mission sur le terrain (position relevée facultative). */
export const schemaChargeDebutMission = z.strictObject({
  position: schemaPosition.optional(),
});

/** Position relevée par l'appareil (trace journalisée, jamais affichée aux usagers). */
export const schemaChargePosition = z.strictObject({
  position: schemaPosition,
  precision_m: z.number().nonnegative().max(100_000).optional(),
});

// Opérations et réponse ------------------------------------------------------------------------

export const schemaOperationTerrain = z.strictObject({
  id_client: schemaIdClient,
  type: z.enum(TYPES_OPERATION_TERRAIN),
  objet_type: z.enum(OBJETS_OPERATION_TERRAIN),
  objet_id: uuid,
  /** Version de l'objet connue de l'appareil au moment de la saisie. */
  version_attendue: z.number().int().nonnegative(),
  /** Horodatage de la saisie sur l'appareil. */
  enregistre_le: schemaDateHeure,
  /** Charge propre au type (schemaChargeRapportMission, schemaChargeDebutMission, schemaChargePosition ; partie B : réalisation de tournée, constat). */
  charge: z.record(z.string(), z.unknown()),
});
export type OperationTerrain = z.infer<typeof schemaOperationTerrain>;

export const schemaSynchronisationTerrain = z.strictObject({
  operations: z.array(schemaOperationTerrain).min(1).max(MAX_OPERATIONS_PAR_ENVOI),
});
export type SynchronisationTerrain = z.infer<typeof schemaSynchronisationTerrain>;

export const schemaResultatOperationTerrain = z.object({
  id_client: schemaIdClient,
  resultat: z.enum(RESULTATS_OPERATION_TERRAIN),
  /** Motif d'un conflit ou d'un rejet, lisible par l'agent. */
  motif: z.string().optional(),
  /** Version serveur courante de l'objet après traitement. */
  version: z.number().int().optional(),
  /** Identifiant serveur de l'objet créé (rapport, constat, réalisation), ou de l'objet visé. */
  objet_id: uuid.optional(),
});
export type ResultatOperationTerrain = z.infer<typeof schemaResultatOperationTerrain>;

export const schemaReponseSynchronisationTerrain = z.object({
  resultats: z.array(schemaResultatOperationTerrain),
  horodatage: z.string(),
});
export type ReponseSynchronisationTerrain = z.infer<typeof schemaReponseSynchronisationTerrain>;

// Photos ---------------------------------------------------------------------------------------

/** Champs du formulaire multipart de POST /terrain/photos (le fichier JPEG ou PNG est dans le champ « fichier »), une photo par requête, rejouable. */
export const schemaPhotoTerrain = z.object({
  id_client: schemaIdClient,
  objet_type: z.enum(OBJETS_PHOTO_TERRAIN),
  /** Identifiant client de l'objet (rapport, constat...) ou identifiant serveur d'un signalement. */
  objet_id_client: uuid,
  /** Empreinte calculée sur l'appareil ; vérifiée par le serveur si elle est fournie. */
  empreinte_sha256: z.string().regex(/^[0-9a-f]{64}$/, 'Empreinte SHA-256 hexadécimale attendue').optional(),
  legende: z.string().trim().max(200).optional(),
});
export type PhotoTerrain = z.infer<typeof schemaPhotoTerrain>;

export const schemaReponsePhotoTerrain = z.object({
  id_client: schemaIdClient,
  resultat: z.enum(['appliquee', 'ignoree_doublon']),
  document_id: uuid,
});

// Charge de travail (GET /terrain/travail) -----------------------------------------------------

export const schemaFiltreTravailTerrain = z.strictObject({
  limite: z.coerce.number().int().min(1).max(LIMITE_TRAVAIL_PAR_OBJET).default(LIMITE_TRAVAIL_PAR_OBJET),
  /** Curseurs de pagination par objet : identifiant du dernier élément reçu. */
  missions_apres: uuid.optional(),
  tournees_apres: uuid.optional(),
  ordres_apres: uuid.optional(),
});

/** Page d'objets emportés : éléments, total et curseur du suivant (nul quand tout est servi). */
export function schemaPageTravail<T extends z.ZodTypeAny>(element: T) {
  return z.object({ elements: z.array(element), total: z.number().int(), suivant: uuid.nullable() });
}

/** Mission telle qu'elle est emportée sur l'appareil : intervention, consignes, version, rapports déjà reçus. */
export const schemaMissionTerrain = z.object({
  id: uuid,
  version: z.number().int(),
  etat: z.enum(ETATS_MISSION),
  date: z.string(),
  consignes: z.string().nullable(),
  equipe: z.object({ id: uuid, nom: z.string(), type: z.enum(['municipale', 'prestataire']) }),
  agent_appartenance_id: uuid.nullable(),
  intervention: z.object({
    id: uuid,
    numero: z.string(),
    titre: z.string(),
    categorie: z.enum(CODES_CATEGORIES_SIGNALEMENT),
    priorite: z.enum(CODES_PRIORITES_INTERVENTION),
    etat: z.string(),
    description: z.string().nullable(),
    adresse: z.string().nullable(),
    position: schemaPosition.nullable(),
    service: z.object({ id: uuid, nom: z.string() }).nullable(),
    signalement: z.object({ id: uuid, numero: z.string(), description: z.string(), photos: z.array(uuid) }).nullable(),
  }),
  rapports: z.array(
    z.object({ id: uuid, type: z.enum(TYPES_RAPPORT_MISSION), etat: z.enum(ETATS_RAPPORT_MISSION), id_client: uuid.nullable(), recu_le: z.string(), photos: z.array(uuid) }),
  ),
  modifiee_le: z.string(),
});
export type MissionTerrain = z.infer<typeof schemaMissionTerrain>;

/**
 * Charge complète : missions (A), tournées et ordres de mission (B, formes de proprete.ts et
 * inspections.ts), référentiels, horodatage serveur. Chaque page porte son curseur « suivant » : le
 * client redemande avec missions_apres, tournees_apres ou ordres_apres tant qu'il n'est pas nul.
 */
export const schemaTravailTerrain = z.object({
  horodatage: z.string(),
  agent: z.object({
    appartenance_id: uuid,
    nom: z.string(),
    equipes: z.array(z.object({ id: uuid, nom: z.string(), type: z.enum(['municipale', 'prestataire']), role: z.enum(['chef', 'membre']) })),
    /** Vrai pour un compte de prestataire (D-043) : seules les missions et tournées de son équipe sont servies. */
    prestataire: z.boolean(),
  }),
  missions: schemaPageTravail(schemaMissionTerrain),
  tournees: schemaPageTravail(schemaTourneeTerrain),
  ordres_mission: schemaPageTravail(schemaOrdreMissionTerrain),
  referentiels: z.object({
    categories: z.array(z.object({ code: z.string(), libelle: z.string() })),
    priorites: z.array(z.object({ code: z.string(), libelle: z.string() })),
    /** Formulaires de constat actifs (partie B), version courante. */
    formulaires: z.array(schemaFormulaireTerrain),
  }),
});
export type TravailTerrain = z.infer<typeof schemaTravailTerrain>;
export type PageTravailTerrain<T> = { elements: T[]; total: number; suivant: string | null };
export type ReponsePhotoTerrain = z.infer<typeof schemaReponsePhotoTerrain>;

// Conflits -------------------------------------------------------------------------------------

export const DECISIONS_CONFLIT_TERRAIN = ['retenir', 'ecarter'] as const;
export type DecisionConflitTerrain = (typeof DECISIONS_CONFLIT_TERRAIN)[number];

/** POST /terrain/conflits/:id/trancher : retenir la version de l'appareil ou l'écarter ; les deux versions restent lisibles. */
export const schemaTranchementConflit = z.strictObject({
  decision: z.enum(DECISIONS_CONFLIT_TERRAIN),
  motif: z.string().trim().max(1000).optional(),
});
export type TranchementConflit = z.infer<typeof schemaTranchementConflit>;

export const schemaFiltreConflitsTerrain = z.strictObject({
  /** en_attente (défaut) : conflits à trancher ; tous : conflits tranchés compris. */
  etat: z.enum(['en_attente', 'tous']).default('en_attente'),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});
